import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Avatar from '@components/dataDisplay/Avatar';
import { CollectionPath } from '@typings/collections.enum';
import { useDocument } from '@nandorojo/swr-firestore';
import { User } from '@typings/setting/userType.interface';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import UserTitle from './UserTitle';

const UserAvatarWrap = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  border-radius: 8px;
  width: fit-content;

  &:hover {
    background-color: var(--inkColor);
    cursor: var(--hoverCursorURL), pointer;
  }
`;

const UserSec = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;

  .description {
    color: var(--subTextColor);
    font-size: 12px;
  }
`;

const NameOnlyWrap = styled.div`
  color: var(--textColor);
  font-size: 14px;
  cursor: var(--hoverCursorURL), pointer;
  transition: color 0.2s;

  &:hover {
    color: var(--primaryColor);
  }
`;

type Props = {
  id?: string;
  uid: string;
  avatarSize?: string;
  style?: React.CSSProperties;
  description?: string;
  shape?: 'token' | 'full' | 'name-only'; // 🆕 name-only 추가
  onClick?: () => void;
  noLink?: boolean;
  closeClick?: (e?) => void;
  showEmail?: boolean;
  showTitleImg?: boolean;
  anonymousName?: string;
  showDisplayName?: boolean;
};
function UserAvatar({
  id,
  uid,
  avatarSize = '26px',
  style,
  onClick,
  closeClick,
  noLink = false,
  description,
  shape = 'token',
  showEmail = false,
  showTitleImg = false,
  anonymousName,
  showDisplayName = true,
}: Props) {
  const { data } = useDocument<User>(`${CollectionPath.USERS}/${uid}`, {
    listen: true,
  });
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (onClick) onClick();
    if (!noLink) navigate(`/user/${uid}/userDetail`);
  }, [onClick, noLink]);

  const shapeMap = {
    token: (
      <UserAvatarWrap id={uid} onClick={handleClick}>
        {showTitleImg && <UserTitle titleID={data?.defaultTitleImg} />}
        {data && <Avatar photoURL={data?.photoURL} size={avatarSize} />}
        {showDisplayName && (
          <div style={style}>{anonymousName || data?.displayName || ''}</div>
        )}
      </UserAvatarWrap>
    ),
    full: (
      <UserAvatarWrap
        id={uid}
        onClick={handleClick}
        style={{ padding: '6px 0' }}
      >
        {data && <Avatar photoURL={data?.photoURL} size={avatarSize || 40} />}
        <UserSec>
          {showDisplayName && (
            <div style={style}>{anonymousName || data?.displayName || ''}</div>
          )}
          {/* {description && <div className="description">{description}</div>} */}
          {showEmail && (
            <div className="description">
              {description}
              {data?.email || ''}
            </div>
          )}
        </UserSec>
        {closeClick && (
          <CloseBtn
            shape="stroke"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              closeClick();
            }}
          />
        )}
      </UserAvatarWrap>
    ),
    'name-only': (
      <NameOnlyWrap id={uid} onClick={handleClick} style={style}>
        {anonymousName || data?.displayName || '사용자'}
      </NameOnlyWrap>
    ),
  };

  return <>{shapeMap[shape]}</>;
}

export default React.memo(UserAvatar);
